import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../store'
import {
  CrossClientQueryExecution,
  CrossClientQueryPhase,
  CrossClientQueryPhases,
  CrossClientQueryResult,
  CrossClientQueryResultDisplay,
  CrossClientQueryResultMatch,
  CrossClientQueryResultMatches,
  CrossClientQueryResultStatus,
  CrossClientQueryResultStatuses,
} from '../../models/CrossClient'
import { User } from '../../models/User'
import { evaluateLogicalFormula } from '../../views/CrossClient/Evaluation/evaluation'
import { mapEvaluatedDecisionToMatch } from '../../views/CrossClient/utils'
import { getUserDisplayName } from '../../apps/professional-app/fa/common/aifeatures/utils'

const FINAL_RESULT_STATUSES: CrossClientQueryResultStatus[] = [
  CrossClientQueryResultStatuses.SUCCEEDED,
  CrossClientQueryResultStatuses.NO_DATA,
  CrossClientQueryResultStatuses.FAILED,
]

interface CrossClientResultsFilter {
  matches: CrossClientQueryResultMatch[]
  clientName: string | null
}

interface CrossClientResultsState {
  execution: CrossClientQueryExecution | null
  executionLoading: boolean
  results: EntityState<CrossClientQueryResultDisplay, string>
  filter: CrossClientResultsFilter
  clients: User[]
  teammates: User[]
  usersLoading: boolean
  pollingEnabled: boolean
}

const crossClientResultsAdapter = createEntityAdapter<CrossClientQueryResultDisplay>()

const initialState: CrossClientResultsState = {
  execution: null,
  executionLoading: true,
  results: crossClientResultsAdapter.getInitialState(),
  filter: { matches: Object.values(CrossClientQueryResultMatches), clientName: null },
  clients: [],
  teammates: [],
  usersLoading: true,
  pollingEnabled: true,
}

export const crossClientResultsSlice = createSlice({
  name: 'crossClientResults',
  initialState,
  reducers: {
    setCrossClientResultsExecutionLoading: (state, action: PayloadAction<boolean>) => {
      state.executionLoading = action.payload
    },
    startCrossClientExecutionPolling: (state) => {
      state.pollingEnabled = true
    },
    stopCrossClientExecutionPolling: (state) => {
      state.pollingEnabled = false
    },
    setCrossClientResults: (state, action: PayloadAction<CrossClientQueryResult[]>) => {
      const { decomposition } = state.execution ?? {}
      if (!decomposition) {
        console.error('Cannot set cross client results without decomposition')
        return
      }
      const displayResults = action.payload
        .filter((r) => FINAL_RESULT_STATUSES.includes(r.status))
        .map((result): CrossClientQueryResultDisplay => {
          if (!result.answers) {
            return {
              ...result,
              match: CrossClientQueryResultMatches.PARTIAL, // fixme
            }
          }
          return {
            ...result,
            match: mapEvaluatedDecisionToMatch(evaluateLogicalFormula(decomposition, result.answers)),
          }
        })
      crossClientResultsAdapter.setMany(state.results, displayResults)
    },
    setCrossClientResultsExecution: (state, action: PayloadAction<CrossClientQueryExecution | null>) => {
      state.execution = action.payload
    },
    setCrossClientResultsExecutionPhase: (state, action: PayloadAction<CrossClientQueryPhase>) => {
      if (state.execution) {
        state.execution.phase = action.payload
      }
    },
    setCrossClientClients: (state, action: PayloadAction<User[]>) => {
      state.clients = action.payload
    },
    setCrossClientTeammates: (state, action: PayloadAction<User[]>) => {
      state.teammates = action.payload
    },
    setCrossClientUsersLoading: (state, action: PayloadAction<boolean>) => {
      state.usersLoading = action.payload
    },
    setCrossClientResultsMatchFilter: (state, action: PayloadAction<CrossClientQueryResultMatch[]>) => {
      state.filter.matches = action.payload
    },
    setCrossClientResultsClientNameFilter: (state, action: PayloadAction<string | null>) => {
      state.filter.clientName = action.payload
    },
    resetCrossClientResults: () => initialState,
  },
})

export const {
  setCrossClientResultsExecutionLoading,
  setCrossClientResults,
  startCrossClientExecutionPolling,
  stopCrossClientExecutionPolling,
  setCrossClientResultsExecution,
  setCrossClientResultsExecutionPhase,
  setCrossClientClients,
  setCrossClientTeammates,
  setCrossClientUsersLoading,
  setCrossClientResultsMatchFilter,
  setCrossClientResultsClientNameFilter,
  resetCrossClientResults,
} = crossClientResultsSlice.actions

const crossClientResultSelectors = crossClientResultsAdapter.getSelectors((state: RootState) => state.crossClientResults.results)

export const selectCrossClientResults = (state: RootState) => crossClientResultSelectors.selectAll(state)

export const selectCrossClientResultsExecution = (state: RootState) => state.crossClientResults.execution

export const selectCrossClientTeammates = createSelector([(state: RootState) => state.crossClientResults.teammates], (teammates) => {
  return teammates.reduce(
    (teammateMap, teammate) => {
      teammateMap[teammate.id] = teammate
      return teammateMap
    },
    {} as Record<string, User>,
  )
})

export const selectCrossClientClients = createSelector([(state: RootState) => state.crossClientResults.clients], (clients) => {
  return clients.reduce(
    (clientMap, client) => {
      clientMap[client.id] = client
      return clientMap
    },
    {} as Record<string, User>,
  )
})

export const selectCrossClientFilter = (state: RootState) => state.crossClientResults.filter

export const selectCrossClientUsersLoading = (state: RootState) => state.crossClientResults.usersLoading

export const selectShouldPollCrossClientExecutionResults = (state: RootState) => {
  const execution = state.crossClientResults.execution
  if (!execution) {
    return false
  }

  const { phase } = execution

  return phase === CrossClientQueryPhases.READY || phase === CrossClientQueryPhases.PROCESSING
}

export const selectCrossClientResultsPollingEnabled = (state: RootState) => state.crossClientResults.pollingEnabled

export const selectCrossClientResultExecutionLoading = (state: RootState) => state.crossClientResults.executionLoading

export const selectLastCrossClientResultUpdatedAt = createSelector([selectCrossClientResults], (crossClientResults) => {
  if (!crossClientResults.length) {
    return null
  }

  const lastResult = crossClientResults.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0]
  return new Date(lastResult.updatedAt).getTime() / 1000
})

export const selectCrossClientFilteredResults = createSelector(
  [selectCrossClientResults, selectCrossClientClients, selectCrossClientFilter],
  (results, clients, filter) => {
    const { matches, clientName } = filter
    return results
      .filter((results) => {
        if (!clientName) {
          return true
        }
        const client = clients[results.clientId]
        return client && getUserDisplayName(client).toLowerCase().includes(clientName.toLowerCase())
      })
      .filter((result) => matches.includes(result.match))
      .sort((result1, result2) => {
        const client1 = clients[result1.clientId]
        const client2 = clients[result2.clientId]
        return getUserDisplayName(client1).localeCompare(getUserDisplayName(client2))
      })
  },
)

export default crossClientResultsSlice.reducer
